import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { SOURCE_FILE_TYPES } from 'enums'
import { selectMediaRecordingSettingsState } from 'selectors/recording'
import { selectSourceFilesGroup } from 'selectors'
import * as PT from '~/PropTypes'
import * as Actions from '~/actions'
import { useAction } from '~/hooks'
import './BaseAssetsPanel.scss'
import CustomDragLayer from './CustomDragLayer'
import Voiceover from './Voiceover'
import Webcam from './Webcam'
import Livestream from './Livestream'
import { selectSplitSize } from '~/selectors/useInterface'
import { getNumFetchFiles } from '~/Util/sourceFile'
import { loadProjectMedia } from '~/actions/loadProjectMedia'
import { selectActiveProjectLoading } from '~/selectors/projectData'


export default function BaseAssetsPanel(props) {
  const { loadTypes, type, ListComponent, withUploader } = props
  const scrollBarRef = useRef(null)
  const isRetryRequestFiles = useRef(true)
  const scrollY = useRef(0)
  const { media: mediaColumnSize } = useSelector(selectSplitSize)
  const isActiveProjectLoading = useSelector(selectActiveProjectLoading)
  const mediaRecordingOpened = useSelector(selectMediaRecordingSettingsState)
  const mediaRecordingClose = useAction(Actions.mainView.closeMediaRecordingSettings)
  const onLoadProjectMedia = useAction(loadProjectMedia)
  const isProjectMedia = type === SOURCE_FILE_TYPES.PROJECT_MEDIA
  const { id: paramId } = useParams()

  const {
    items,
    allFilesFetched,
    totalCount,
    loading,
  } = useSelector(state => selectSourceFilesGroup(state, type))
  // ---

  const fetchFiles = useAction(Actions.sourceFiles.loadSourceFiles)
  const countFetchSourceElems = useMemo(() => getNumFetchFiles(
    mediaColumnSize, type
  ), [ mediaColumnSize, type ])

  React.useEffect(() => {
    scrollY.current = 0
  }, [ type ])

  React.useEffect(
    () => {
      if (!isActiveProjectLoading) {
        const isRetry = isRetryRequestFiles.current
        if (isProjectMedia && isRetry) {
          onLoadProjectMedia(paramId)
          isRetryRequestFiles.current = false
        } else if (!allFilesFetched || !totalCount || isRetry) {
          let count
          if (items.length === 0 || isRetry) {
            count = withUploader
              ? countFetchSourceElems - 1
              : countFetchSourceElems
          }
          if (count && !loading) {
            fetchFiles(loadTypes ?? type, {
              start: items.length,
              count,
              force: isRetry,
            })
            isRetryRequestFiles.current = false
          }
        }
      }
      return () => {
        isRetryRequestFiles.current = true
      }
    },
    // eslint-disable-next-line
    [ isProjectMedia, isRetryRequestFiles, isActiveProjectLoading, paramId ]
  )

  const onScroll = useCallback(({
    scrollOffset,
    scrollDirection,
  }) => {
    if (!isProjectMedia && __CFG__.SOURCE_FILES_MANAGEMENT.LOAD_MEDIA_ONSCROLL) {
      if (scrollOffset > 0 && scrollDirection === 'forward') {
        if (!allFilesFetched || !totalCount) {
          fetchFiles(type, {
            start: items.length,
            count: countFetchSourceElems,
          })
        }
      }
    }
  }, [ allFilesFetched, fetchFiles, items.length,
    countFetchSourceElems, totalCount, type, isProjectMedia ])

  return (
    <Choose>
      <When condition={mediaRecordingOpened === 'audio'}>
        <Voiceover onClose={mediaRecordingClose} />
      </When>
      <When condition={mediaRecordingOpened === 'video'}>
        <Webcam onClose={mediaRecordingClose} />
      </When>
      <When condition={mediaRecordingOpened === 'live'}>
        <Livestream onClose={mediaRecordingClose} />
      </When>
      <Otherwise>
        <CustomDragLayer />
        <ListComponent
          onScroll={onScroll}
          scrollBarRef={scrollBarRef}
          type={type}
        />
      </Otherwise>
    </Choose>
  )
}

BaseAssetsPanel.defaultProps = {
  loadTypes: undefined,
  withUploader: false,
}

BaseAssetsPanel.propTypes = {
  type: PT.SourceFileType.isRequired,
  loadTypes: PropTypes.arrayOf(PT.SourceFileType),
  ListComponent: PropTypes.elementType.isRequired,
  withUploader: PropTypes.bool,
}
